import { Cog, Handshake, Landmark } from 'lucide-react'
import { AdaptationCycleStageConfig } from '../types'
import adaptationCycleStageOneImage from '@app/assets/illustrations/adaptation-cycle-overview-1.png'

export const adaptationCycleStageOne: AdaptationCycleStageConfig = {
  id: 'stage-one',
  title: 'Stage 1: Context and Preparation',
  description:
    'Stage 1 builds the mandate, relationships, and shared understanding that makes adaptation planning legitimate and workable. It defines the purpose and scope of the process, establishes governance and partnership structures, and designs how engagement and decision-making will occur across the planning cycle.',
  tagline: '',
  heroImage: adaptationCycleStageOneImage,

  outcome: {
    title: 'Outcome of Stage 1',
    description:
      'A shared understanding of the purpose and scope of adaptation planning: who is involved, how decisions are made, how knowledge and relationships will be maintained over time, and what success looks like.',
  },
  topParagraphs: [
    'People coming to Stage 1 will have different views on what adaptation planning is for, and different roles in delivering it. Engineers, planners, community engagement staff, and iwi representatives all bring different priorities and ways of framing the work, and that is appropriate. The three streams in this guidance reflect that. What matters is that each party understands what the others are contributing and why.',
    'The three activities are mutually reinforcing rather than strictly sequential, and each may be revisited as clarity, relationships, and information develop.',
  ],
  activities: [
    {
      activity: 'Activity 1.1',
      forumSlug: 'establish-purpose-and-scope',
      title: 'Establish Purpose and Scope',
      mfeAlignment: [{ step: 1, label: 'Set the context and prepare' }],
      description:
        'This activity defines why the work is being undertaken, who has decision-making authority, and what geographic, temporal, and organisational scope it will cover.',
      streams: [
        // Keep to 3 streams
        {
          icon: Landmark,
          title: 'GOVERNANCE',
          points: [
            'Before deciding on a governance structure, clarify its purpose: securing elected member buy-in, horizontal support across departments, external legitimacy, or some combination.',
            'Establish a governance structure appropriate to that purpose (e.g. a steering group, working group, or programme board).',
            'Confirm mandate, purpose, and scope (geographic, temporal, organisational).',
            'Determine how you will secure horizontal support across departments.',
            'Secure vertical organisational support (executive or elected member buy-in).',
            'Agree how and when risk tolerance thresholds will be set and how community voice will be incorporated in those decisions.',
            'Set parameters for decision-making, transparency, and review.',
          ],
        },
        {
          icon: Handshake,
          title: 'PARTNERSHIPS',
          points: [
            'Confirm budget and resourcing before establishing the project team and governance structures.',
            'Establish the project team and identify key internal contacts.',
            'Establish a Technical Advisory Group (TAG) for specialist expertise and technical review.',
            'Draft an initial project plan covering key documents, timelines, and statutory interfaces.',
            'Clarify how analysis outputs will be used and integrated into business-as-usual processes, whether statutory planning, asset management, adaptation pathways, or some combination.',
            'Determine openness to different forms of adaptive planning (DAP, DAPP, or hybrid), recognising that the final choice will depend on scale, assets, and organisational context, and will be confirmed later in Stage 3.',
          ],
        },
        {
          icon: Cog,
          title: 'TECHNICAL',
          points: [
            'Engage iwi and partners in defining mandate and scope, in alignment with Activity 1.2.',
            'Confirm who needs to be at the table for the process to be legitimate.',
            'Map the broader network of relevant expertise, including other councils, CCOs, regional and central government agencies, iwi authorities, and universities, and determine how each will be engaged across the programme.',
          ],
        },
      ],
      outputs: [
        {
          text: 'Project charter and scope statement.',
        },
        {
          text: 'Governance structure documentation (terms of reference, decision-rights framework).',
        },
        {
          text: 'Partnership agreements or memorandums of understanding.',
        },
        {
          text: 'Initial project plan.',
        },
      ],
      connections: [
        'Draws on existing strategic, statutory, and climate policy documents to ensure alignment with broader organisational objectives.',
        'Developed in parallel with 1.2 and 1.3.',
        'Establishes decision rights and resourcing needed for Stage 2.',
        'Underpins adaptive strategy adoption and implementation (3.6 and 4.1) by defining authority, funding, and review responsibilities.',
      ],
      relatedActivities: [
        { stageId: 'stage-one', activity: 'Activity 1.2', label: '1.2' },
        { stageId: 'stage-one', activity: 'Activity 1.3', label: '1.3' },
        { stageId: 'stage-two', label: 'Stage 2' },
        {
          stageId: 'stage-three',
          activity: 'Activity 3.6',
          label: '3.6',
        },
        {
          stageId: 'stage-four',
          activity: 'Activity 4.1',
          label: '4.1',
        },
      ],
      moveOnWhen: [
        'Budget and resourcing secured.',
        'Governance structure established with clear purpose and membership.',
        'Project team and TAG in place.',
        'Organisational support confirmed, vertical and horizontal.',
        'Partnership agreements in place with key iwi and organisations.',
      ],
      resources: [
        {
          title: 'Risk',
          articles: [
            {
              title: 'What Is Risk',
              description: '',
              href: '/app/guide/fundamentals/risk-consequences-and-uncertainty/what-is-risk',
            },
            {
              title: 'The Planning Process for Risk',
              description: '',
              href: '/app/guide/fundamentals/planning-and-implementation/the-planning-process-for-risk',
            },
            {
              title: 'What Are Consequences',
              description: '',
              href: '/app/guide/fundamentals/risk-consequences-and-uncertainty/what-are-consequences',
            },
            {
              title: 'What Is Systemic Risk? And Why Does It Matter for Communities?',
              description: '',
              href: '/app/guide/insights/research-and-methods/what-is-systemic-risk-and-why-does-it-matter-for-communities',
            },
          ],
        },
        {
          title: 'Adaptation Purpose',
          articles: [
            {
              title: 'What Is Climate Change Adaptation',
              description: '',
              href: '/app/guide/fundamentals/adaptation-and-system-change/what-is-climate-change-adaptation',
            },
            {
              title: 'Climate Mitigation and Climate Adaptation',
              description: '',
              href: '/app/guide/fundamentals/climate-futures-and-scenarios/climate-mitigation-and-climate-adaptation',
            },
            {
              title: 'Climate Change: What You Might Need to Think About',
              description: '',
              href: '/app/guide/insights/commentary-and-opinion/climate-change-what-you-might-need-to-think-about',
            },
          ],
        },
        {
          title: 'Uncertainty and Scenarios',
          articles: [
            {
              title: 'Types of Uncertainty',
              description: '',
              href: '/app/guide/fundamentals/risk-consequences-and-uncertainty/types-of-uncertainty',
            },
            {
              title: 'Using Scenarios to Explore Uncertain Futures in Planning',
              description: '',
              href: '/app/guide/fundamentals/climate-futures-and-scenarios/using-scenarios-to-explore-uncertain-futures-in-planning',
            },
          ],
        },
        {
          title: 'NZ Legislation and Policy',
          articles: [
            {
              title: 'Local vs Regional Roles Under the RMA',
              description: '',
              href: '/app/guide/fundamentals/values-participation-and-governance/local-vs-regional-roles-under-the-rma',
            },
          ],
        },
      ],
    },
    {
      activity: 'Activity 1.2',
      forumSlug: 'engage-stakeholders-and-build-relationships',
      title: 'Establish Engagement and Partnerships',
      mfeAlignment: [{ step: 1, label: 'Set the context and prepare' }],
      description:
        'This activity establishes the relationships and processes that give legitimacy to adaptation planning. The key focus at this point is on key organisations, iwi, and sector representatives who will shape governance and technical structures, rather than broad public communication or producing highly detailed plans. The aim is to agree on how engagement will work across the whole process (principles, structure, and resourcing) rather than to produce a complete plan upfront. Effective engagement in an adaptation context is about building trust and shared understanding, rather than solely delivering information.',
      streams: [
        // Keep to 3 streams
        {
          icon: Landmark,
          title: 'GOVERNANCE',
          points: [
            'Endorse the principles and purpose of engagement.',
            'Confirm that governance arrangements provide sufficient mandate and resourcing for the engagement approach.',
            'Note that sign-off on engagement for each subsequent phase will occur progressively as the programme develops.',
          ],
        },
        {
          icon: Handshake,
          title: 'PARTNERSHIPS',
          points: [
            'Connect with key organisations, iwi, mana whenua, and sector representatives who will shape or participate in governance and technical structures.',
            'Confirm who needs to be involved at each stage for the process to be legitimate.',
            'Co-design engagement principles and approaches with these partners for use across later stages.',
            'Map stakeholders relevant to governance structures.',
          ],
        },
        {
          icon: Cog,
          title: 'TECHNICAL',
          points: [
            'Draft an initial engagement framework identifying key engagement principles, a no-surprises approach to communication, the range of engagement tactics available at different stages, and resourcing requirements.',
            'Identify statutory engagement requirements relevant to the programme.',
            'Note that detailed engagement plans are typically developed per phase rather than in full at this stage.',
            'Consider who in the team can bridge between technical risk analysis and community and political decision-making.',
          ],
        },
      ],
      outputs: [
        {
          text: 'Engagement framework (principles, approach, available tactics, and resourcing).',
        },
        {
          text: 'Stakeholder map (organisations, iwi, sector representatives).',
        },
        {
          text: 'Initial partnership agreements for participation in governance and technical structures, where relevant.',
        },
      ],
      connections: [
        'Works iteratively with 1.1 and 1.3 to ensure engagement principles are embedded in governance and reflected in the Guiding Framework.',
        'Provides the stakeholder and partnership framework that supports values identification (2.2) and legitimacy for risk assessment and options evaluation (2.1 to 3.4).',
        'Establishes relationships sustained into later stages, including focus-area planning (Stage 3) and monitoring (4.2).',
      ],
      relatedActivities: [
        { stageId: 'stage-one', activity: 'Activity 1.1', label: '1.1' },
        { stageId: 'stage-one', activity: 'Activity 1.3', label: '1.3' },
        {
          stageId: 'stage-two',
          activity: 'Activity 2.1',
          label: '2.1',
        },
        {
          stageId: 'stage-two',
          activity: 'Activity 2.2',
          label: '2.2',
        },
        {
          stageId: 'stage-three',
          activity: 'Activity 3.4',
          label: '3.4',
        },
        { stageId: 'stage-three', label: 'Stage 3' },
        {
          stageId: 'stage-four',
          activity: 'Activity 4.2',
          label: '4.2',
        },
      ],
      moveOnWhen: [
        'Governance has endorsed engagement principles and the approach.',
        'Key partnerships confirmed and initial agreements in place.',
        'Stakeholder map complete.',
        'Engagement framework agreed, including resourcing for this stage.',
      ],
      resources: [
        {
          title: 'Who to Involve',
          articles: [
            {
              title: 'Engagement with Māori',
              description: '',
              href: '/app/guide/fundamentals/values-participation-and-governance/engagement-with-maori',
            },
            {
              title: 'Community Values in Adaptation',
              description: '',
              href: '/app/guide/fundamentals/values-participation-and-governance/community-values-in-adaptation',
            },
            {
              title: 'Relational Risk, Values, and Worldviews',
              description: '',
              href: '/app/guide/fundamentals/values-participation-and-governance/relational-risk-values-and-worldviews-how-values-shape-what-we-see-as-at-risk',
            },
          ],
        },
        {
          title: 'Designing the Engagement Process',
          articles: [
            {
              title: 'Community Engagement Principles',
              description: '',
              href: '/app/guide/fundamentals/values-participation-and-governance/community-engagement-principles',
            },
          ],
        },
        {
          title: 'Partnerships and Māori Engagement',
          articles: [
            {
              title: 'Engagement with Māori',
              description: '',
              href: '/app/guide/fundamentals/values-participation-and-governance/engagement-with-maori',
            },
            {
              title: 'Protection of Māori Rights',
              description: '',
              href: '/app/guide/fundamentals/values-participation-and-governance/protection-of-maori-rights',
            },
            {
              title: 'Climate Justice in Adaptation',
              description: '',
              href: '/app/guide/fundamentals/values-participation-and-governance/climate-justice-in-adaptation',
            },
          ],
        },
        {
          title: 'Trust and Pitfalls',
          articles: [
            {
              title: 'Foundations of Risk Perception',
              description: '',
              href: '/app/guide/fundamentals/risk-consequences-and-uncertainty/foundations-of-risk-perception',
            },
            {
              title: 'Countering Science Denial',
              description: '',
              href: '/app/guide/fundamentals/decisions-and-behaviour/countering-science-denial',
            },
            {
              title: 'Heuristics and Cognitive Biases in Risk Decisions',
              description: '',
              href: '/app/guide/fundamentals/decisions-and-behaviour/heuristics-and-cognitive-biases',
            },
            {
              title: 'Framing Effects in Risk Communication',
              description: '',
              href: '/app/guide/fundamentals/decisions-and-behaviour/framing-effects-in-risk-communication',
            },
          ],
        },
      ],
    },
    {
      activity: 'Activity 1.3',
      forumSlug: 'develop-guiding-framework-and-gather-baseline-information',
      title: 'Establish the Guiding Framework and Baseline',
      mfeAlignment: [{ step: 1, label: 'Set the context and prepare' }],
      description:
        'This activity formalises the agreements reached in Activities 1.1 and 1.2 into a shared reference document, and collates existing technical knowledge and prior reports to avoid duplicating past work. The Guiding Framework is a living document and will evolve as relationships, knowledge, and priorities develop across the planning cycle.',
      streams: [
        // Keep to 3 streams
        {
          icon: Landmark,
          title: 'GOVERNANCE',
          points: [
            'Provide feedback on how the draft Guiding Framework reflects agreed purpose, scope, and guiding principles.',
            'Confirm that monitoring, transparency, and review mechanisms are in place before public release.',
            'Endorse the final Framework as the formal basis for adaptation planning once technical and partnership input is complete.',
            'Confirm public consultation has occurred and community feedback has been considered before finalising.',
          ],
        },
        {
          icon: Handshake,
          title: 'PARTNERSHIPS',
          points: [
            'Co-develop guiding principles with iwi and Māori partners and key stakeholders, ensuring consistency with the engagement approach agreed in 1.2.',
            'Agree on how mātauranga Māori, lived experience, and technical knowledge will be treated alongside one another in the Framework.',
            'Confirm partnership roles and expectations in the Framework.',
          ],
        },
        {
          icon: Cog,
          title: 'TECHNICAL',
          points: [
            'Draft the Guiding Framework, covering roles and responsibilities, guiding principles, staged (area-by-area) process where relevant, and a flexible iterative process for engagement and decision-making.',
            'Collate existing assessments, reports, and planning documents (hazard studies, adaptation plans, statutory plans).',
            'Compile historical events and lived experiences (past flood and storm impacts, community narratives, oral histories).',
            'Collate and evaluate hazard datasets for suitability, recording spatial and temporal coverage, assumptions, resolution, and any gaps to be carried into the risk assessment.',
            'Record provenance of baseline datasets to support transparency and shared ownership.',
          ],
        },
      ],
      outputs: [
        {
          text: 'Public-facing Guiding Framework (roles, guiding principles, process, and intent).',
        },
        {
          text: 'Indigenous Knowledge Management Plan (protocols for storage, access, sharing, and IP protection of mātauranga Māori and indigenous knowledge).',
        },
        {
          text: 'Integrated Stage 1 summary (mandate, governance, engagement architecture, and baseline data register).',
        },
        {
          text: 'Supporting baseline summary (hazards, existing reports, historical events).',
        },
      ],
      connections: [
        'Synthesises and documents agreements developed through 1.1 and 1.2, forming a shared reference for technical and governance continuity.',
        'Informs the broad-scale hazard and exposure assessment (2.1) and ensures consistent data standards.',
        'Establishes the baseline for later monitoring (4.2) and review (4.3).',
      ],
      relatedActivities: [
        { stageId: 'stage-one', activity: 'Activity 1.1', label: '1.1' },
        { stageId: 'stage-one', activity: 'Activity 1.2', label: '1.2' },
        {
          stageId: 'stage-two',
          activity: 'Activity 2.1',
          label: '2.1',
        },
        {
          stageId: 'stage-four',
          activity: 'Activity 4.2',
          label: '4.2',
        },
        {
          stageId: 'stage-four',
          activity: 'Activity 4.3',
          label: '4.3',
        },
      ],
      moveOnWhen: [
        'Framework drafted and jointly reviewed by governance and partners.',
        'Guiding principles agreed in partnership.',
        'Any consultation or targeted engagement required for the Guiding Framework has been completed and documented.',
        'Framework formally endorsed by governance and released as the basis for adaptation planning.',
        'Hazard dataset register is complete, with gaps documented and a plan in place to address critical ones. Do not wait for perfect information before proceeding.',
      ],
      resources: [
        {
          title: 'Building the Framework',
          articles: [
            {
              title: 'Climate Change: What You Might Need to Think About',
              description: '',
              href: '/app/guide/insights/commentary-and-opinion/climate-change-what-you-might-need-to-think-about',
            },
            {
              title: 'The Planning Process for Risk',
              description: '',
              href: '/app/guide/fundamentals/planning-and-implementation/the-planning-process-for-risk',
            },
            {
              title: 'Risk Treatment',
              description: '',
              href: '/app/guide/fundamentals/adaptation-and-system-change/risk-treatment',
            },
            {
              title: 'Community Engagement Principles',
              description: '',
              href: '/app/guide/fundamentals/values-participation-and-governance/community-engagement-principles',
            },
          ],
        },
        {
          title: 'Principles and Knowledge Systems',
          articles: [
            {
              title: 'Engagement with Māori',
              description: '',
              href: '/app/guide/fundamentals/values-participation-and-governance/engagement-with-maori',
            },
            {
              title: 'Mātauranga Māori (Māori Knowledge)',
              description: '',
              href: '/app/guide/fundamentals/values-participation-and-governance/matauranga-maori-maori-knowledge',
            },
            {
              title: 'Protection of Māori Rights',
              description: '',
              href: '/app/guide/fundamentals/values-participation-and-governance/protection-of-maori-rights',
            },
            {
              title: 'Community Values in Adaptation',
              description: '',
              href: '/app/guide/fundamentals/values-participation-and-governance/community-values-in-adaptation',
            },
            {
              title: 'Relational Risk, Values, and Worldviews',
              description: '',
              href: '/app/guide/fundamentals/values-participation-and-governance/relational-risk-values-and-worldviews-how-values-shape-what-we-see-as-at-risk',
            },
          ],
        },
        {
          title: 'Baseline Information and Narratives',
          articles: [
            {
              title: 'Hazards and Environmental Change',
              description: '',
              href: '/app/guide/fundamentals/hazards-and-stressors/hazards-and-environmental-change',
            },
            {
              title: 'Community Values in Adaptation',
              description: '',
              href: '/app/guide/fundamentals/values-participation-and-governance/community-values-in-adaptation',
            },
            {
              title: 'Climate Justice in Adaptation',
              description: '',
              href: '/app/guide/fundamentals/values-participation-and-governance/climate-justice-in-adaptation',
            },
            {
              title: 'Why Climate Adaptation Needs a Systems View of Wellbeing',
              description: '',
              href: '/app/guide/insights/commentary-and-opinion/why-climate-adaptation-needs-a-systems-view-of-wellbeing',
            },
          ],
        },
        {
          title: 'Examples and Case Studies',
          articles: [
            {
              title: "Christchurch City Council's Coastal Adaptation Framework",
              description: '',
              href: 'https://ccc.govt.nz/assets/Documents/Environment/Coast/CoastalAdaptationFramework0522.pdf',
            },
          ],
        },
      ],
    },
  ],
  previousStage: null,
  nextStage: 'stage-two',
}
